import {
  Bot,
  Database,
  GitBranch,
  Grid3x3,
  HelpCircle,
  History,
  Key,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Server,
  Settings,
  Shield,
  Target,
  Users,
  Wrench,
} from "lucide-react";
import type { FeatureFlag } from "@/config/featureFlags";
import type { Section } from "./HelpComponents";
import { Callout, FeatureGrid, FeatureGridItem, Kbd, SectionCard, StepCard } from "./HelpComponents";

/** Feature-flag-aware section builder. Accepts a set of enabled flags so that
 *  flagged content (Getting Started steps, Dashboard references, entire
 *  sections) is omitted when the corresponding feature is turned off. */
export function getHelpSections(enabledFlags: Record<FeatureFlag, boolean>): Section[] {
  const on = (flag: FeatureFlag) => enabledFlags[flag];

  // -- Getting Started steps (conditional + auto-numbered) ------------------
  const gettingStartedSteps: { flag?: FeatureFlag; title: string; body: React.ReactNode }[] = [
    {
      title: "Pick your team",
      body: (
        <>
          Use the team selector in the header to choose which team you're working with. Your team determines which
          shared agents and data sources you can access.
        </>
      ),
    },
    {
      title: "Start a conversation",
      body: (
        <>
          Head over to Chat and say hello. Choose an AI model, optionally pick an agent, and start typing — it's that
          simple.
        </>
      ),
    },
    {
      flag: "brainstorm",
      title: "Brainstorm with multiple agents",
      body: (
        <>
          Got a tough problem? Open Brainstorm and pose your question to a panel of AI specialists who discuss it from
          different angles — like having your own advisory board.
        </>
      ),
    },
    {
      flag: "autopilot",
      title: "Set a goal with Autopilot",
      body: (
        <>
          Describe what you want done and let Autopilot plan and execute it step by step — with artifacts, progress
          tracking, and human-in-the-loop input when needed.
        </>
      ),
    },
    {
      flag: "canvas",
      title: "Create with Canvas",
      body: (
        <>
          Need a polished document? Canvas generates beautiful HTML documents you can preview, iterate on, and print or
          export to PDF.
        </>
      ),
    },
    {
      title: "Explore your agents",
      body: (
        <>
          Browse the My Agents page to see what's already available or create your very own custom agent with a
          personality and knowledge base.
        </>
      ),
    },
    {
      flag: "workflows",
      title: "Try a Workflow",
      body: (
        <>
          Ready for something powerful? Check out Workflows to chain multiple agents together into automated workflows.
          Think of it as an assembly line for AI tasks.
        </>
      ),
    },
  ];

  const visibleSteps = gettingStartedSteps.filter((s) => !s.flag || on(s.flag));

  // -- Dashboard "What do you want to do?" items (conditional) ---------------
  const dashboardActions: { flag?: FeatureFlag; label: string; desc: string }[] = [
    { label: "Chat", desc: "Start a new conversation with an AI model or agent." },
    { flag: "brainstorm", label: "Brainstorm", desc: "Kick off a multi-agent discussion for diverse perspectives." },
    {
      flag: "autopilot",
      label: "Autopilot",
      desc: "Create a new goal and let AI plan and execute it autonomously.",
    },
    { flag: "canvas", label: "Canvas", desc: "Generate a polished HTML document with AI." },
    { flag: "workflows", label: "Workflows", desc: "Build a multi-step agent pipeline." },
  ];
  const visibleDashboardActions = dashboardActions.filter((a) => !a.flag || on(a.flag));

  // -- Welcome paragraph (varies based on enabled features) ------------------
  const featureDescriptions: string[] = ["chat with intelligent agents"];
  if (on("brainstorm")) featureDescriptions.push("brainstorm with multiple AI specialists");
  if (on("autopilot")) featureDescriptions.push("delegate goals to autonomous agents");
  if (on("canvas")) featureDescriptions.push("generate polished documents with Canvas");
  if (on("workflows")) featureDescriptions.push("build automated workflows");
  featureDescriptions.push("share knowledge with your colleagues");

  const welcomeText = `AnthmAI is your team's AI co-pilot — a platform where you can ${featureDescriptions.join(", ")}, all in one place.`;

  return [
    // -----------------------------------------------------------------------
    // 1. Getting Started
    // -----------------------------------------------------------------------
    {
      id: "getting-started",
      title: "Getting Started",
      subtitle: "Everything you need to hit the ground running",
      icon: HelpCircle,
      content: (
        <div className="space-y-6">
          <SectionCard title="Welcome to AnthmAI" accent="orange">
            <p>
              {welcomeText} Whether you're drafting content, analyzing data, or orchestrating multi-step processes,
              AnthmAI makes it feel effortless.
            </p>
            <p>
              This guide will walk you through every corner of the platform so you can get the most out of it. Let's
              dive in.
            </p>
          </SectionCard>

          <SectionCard title="Up and running in 60 seconds">
            <div className="space-y-4">
              {visibleSteps.map((s, i) => (
                <StepCard key={s.title} step={i + 1} title={s.title}>
                  {s.body}
                </StepCard>
              ))}
            </div>
          </SectionCard>

          <Callout variant="tip">
            <p>
              The sidebar on the left is your navigation home base. You can collapse it with the chevron icon at the
              top, and on mobile just tap the menu icon in the header. Everything is one click away.
            </p>
          </Callout>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 2. Dashboard
    // -----------------------------------------------------------------------
    {
      id: "dashboard",
      title: "Dashboard",
      subtitle: "Your mission control for everything happening on the platform",
      icon: LayoutDashboard,
      content: (
        <div className="space-y-6">
          <SectionCard title="Your home base" accent="orange">
            <p>
              The Dashboard is the first thing you see when you sign in — a bird's-eye view of your activity, your go-to
              agents, and shortcuts to jump straight into action. Think of it as mission control.
            </p>
          </SectionCard>

          <SectionCard title="At-a-glance stats">
            <p>Four cards across the top give you a quick pulse-check on your platform activity:</p>
            <FeatureGrid>
              <FeatureGridItem title="Chat Sessions">
                Total number of conversations you've started so far.
              </FeatureGridItem>
              <FeatureGridItem title="Agents">How many custom agents you've built.</FeatureGridItem>
              {on("autopilot") && (
                <FeatureGridItem title="Autopilot">Number of autonomous goals currently running.</FeatureGridItem>
              )}
              <FeatureGridItem title="Data Sources">
                Knowledge collections you've created for your agents.
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="What do you want to do?">
            <p>
              {visibleDashboardActions.length} action row{visibleDashboardActions.length !== 1 ? "s" : ""} give
              {visibleDashboardActions.length === 1 ? "s" : ""} you one-click shortcuts to every major feature:
            </p>
            <ul className="list-disc list-inside space-y-2">
              {visibleDashboardActions.map((a) => (
                <li key={a.label}>
                  <strong className="text-content-primary">{a.label}</strong> — {a.desc}
                </li>
              ))}
            </ul>
          </SectionCard>

          <SectionCard title="Active Goals & recent activity">
            <p>
              {on("autopilot") && (
                <>
                  Below the action rows, you'll find an <strong className="text-content-primary">Active Goals</strong>{" "}
                  section showing any Autopilot tasks that are currently running — so you can monitor progress or jump
                  in at any time.{" "}
                </>
              )}
              On the right side, a sidebar displays your{" "}
              <strong className="text-content-primary">Recent Conversations</strong> (your 5 most recent chat sessions)
              and <strong className="text-content-primary">Your Agents</strong> (your 5 most recent agents) — click any
              to jump straight in.
            </p>
          </SectionCard>

          <Callout variant="info">
            <p>
              The Dashboard is your launchpad — everything you need is one click away. No hunting through menus
              required.
            </p>
          </Callout>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 3. Chat
    // -----------------------------------------------------------------------
    {
      id: "chat",
      title: "Chat",
      subtitle: "One-on-one conversations with AI that actually get things done",
      icon: MessageSquare,
      content: (
        <div className="space-y-6">
          <SectionCard title="Your AI conversation hub" accent="orange">
            <p>
              Chat is where the magic happens. Pick a model, optionally pair it with a specialized agent, and start
              talking. Your conversation history is saved automatically, so you can pick up right where you left off —
              even days later.
            </p>
          </SectionCard>

          <SectionCard title="Model & agent selection">
            <FeatureGrid>
              <FeatureGridItem title="Model selector">
                Choose which AI model powers the conversation (e.g., GPT-4o). Once you send your first message the model
                locks in — so choose wisely!
              </FeatureGridItem>
              <FeatureGridItem title="Agent selector">
                Pick a specialized agent (e.g., Code Assistant, Data Analyst) to shape the AI's behavior. The agent
                locks after your first message too.
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="File attachments & rich input">
            <p>The input bar supports multiple ways to attach context to your messages:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">File upload</strong> — Click the paperclip button to select
                files from your computer. Multiple files at once are supported.
              </li>
              <li>
                <strong className="text-content-primary">Image upload</strong> — Click the image button to attach images
                for vision-capable models. You can also <strong className="text-content-primary">paste images</strong>{" "}
                directly from your clipboard.
              </li>
              <li>
                <strong className="text-content-primary">SharePoint</strong> — Browse and attach files from your
                organization's SharePoint document libraries without leaving the chat.
              </li>
              <li>
                <strong className="text-content-primary">Voice input</strong> — Click the microphone button to dictate
                your message instead of typing.
              </li>
              <li>
                <strong className="text-content-primary">Drag and drop</strong> — Drop files or images directly onto the
                input area.
              </li>
            </ul>
            <p className="mt-2">Each attachment appears as a chip above the input and can be removed individually.</p>
          </SectionCard>

          <SectionCard title="AI preferences">
            <p>
              The <strong className="text-content-primary">Preferences</strong> button in the header lets you know
              whether you've configured custom AI preferences (response tone, length, custom instructions). When active,
              the button turns <span className="text-brand-secondary font-medium">purple</span> and shows a tooltip
              summarizing your settings. Head to Settings to adjust them anytime.
            </p>
          </SectionCard>

          <SectionCard title="Sending messages & getting responses">
            <ul className="list-disc list-inside space-y-2">
              <li>
                Type your message and press <Kbd>Enter</Kbd> or click{" "}
                <strong className="text-content-primary">Send</strong>.
              </li>
              <li>
                Your message appears immediately while the AI streams its response in real-time with a pulsing cursor.
              </li>
              <li>
                Responses render with full <strong className="text-content-primary">Markdown</strong> —
                syntax-highlighted code blocks, tables, lists, blockquotes, links, the works.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Message actions">
            <p>Below each AI response you'll find two handy action buttons:</p>
            <FeatureGrid>
              <FeatureGridItem title="Copy">
                Copies the response to your clipboard. A green checkmark confirms it worked.
              </FeatureGridItem>
              <FeatureGridItem title="Regenerate">
                Re-sends your last message for a fresh response. The original reply (and anything after it) is removed.
                Disabled while the AI is streaming.
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Sharing conversations" accent="blue">
            <p>
              Click <strong className="text-content-primary">Share</strong> (visible on your own sessions) to open the
              share dialog. Two options:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Share with People</strong> — Search for users by name or email
                and grant <strong className="text-content-primary">Read</strong> (view only) or{" "}
                <strong className="text-content-primary">Read & Write</strong> permission. You can see who currently has
                access and revoke it.
              </li>
              <li>
                <strong className="text-content-primary">Share Link</strong> — Generate a shareable URL with a chosen
                permission level. Copy it to your clipboard or deactivate it anytime.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Exporting conversations" accent="green">
            <p>
              Click the <strong className="text-content-primary">Export</strong> button in the chat header to download
              your conversation. The export modal lets you customize the output:
            </p>
            <FeatureGrid>
              <FeatureGridItem title="Format">Choose between DOCX, PDF, or Markdown.</FeatureGridItem>
              <FeatureGridItem title="Template">
                Pick "Clean Minimal" for a simple look, or "Branded" for a styled export with platform branding.
              </FeatureGridItem>
            </FeatureGrid>
            <p className="mt-3">
              You can also toggle whether to include system messages in the export. Click{" "}
              <strong className="text-content-primary">Export</strong> to download the file.
            </p>
          </SectionCard>

          <Callout variant="info" title="What happens with shared sessions?">
            <p>
              When you open a session someone shared with you, a banner shows who shared it and your permission level.{" "}
              <strong className="text-content-primary">Read Only</strong> means the input area is disabled.{" "}
              <strong className="text-content-primary">Read & Write</strong> automatically <em>forks</em> the chat when
              you send a message — you get your own copy, and the original stays untouched.
            </p>
          </Callout>

          <SectionCard title="Chat history sidebar">
            <p>
              On the right side of the chat area (desktop only), a collapsible sidebar keeps your recent conversations
              within reach:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">My Chats</strong> — Your 20 most recent sessions with titles,
                message counts, and timestamps. Click any to jump to it.
              </li>
              <li>
                <strong className="text-content-primary">Shared</strong> — Chats shared with you, including who shared
                them and when. Only appears when you have shared sessions.
              </li>
              <li>
                <strong className="text-content-primary">View All History</strong> — Link at the bottom to open the full
                History page.
              </li>
            </ul>
          </SectionCard>

          <Callout variant="tip">
            <p>
              Click the <strong className="text-content-primary">New Chat</strong> button in the header at any time to
              start fresh. It resets the model, agent, and file selections — a clean slate.
            </p>
          </Callout>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 4. Brainstorm (Group Chat)
    // -----------------------------------------------------------------------
    {
      id: "group-chat",
      title: "Brainstorm",
      subtitle: "Get multiple perspectives from AI specialists",
      icon: MessagesSquare,
      featureFlag: "brainstorm",
      content: (
        <div className="space-y-6">
          <SectionCard title="AI teamwork at its best" accent="purple">
            <p>
              Brainstorm is one of the most exciting features of AnthmAI. Instead of talking to a single agent, you pose
              a question and multiple specialist agents discuss it from different angles — an analyst, a strategist, a
              technical expert — giving you comprehensive, multi-perspective insights. It's like having a whole advisory
              board on standby.
            </p>
          </SectionCard>

          <SectionCard title="The Persona Bench" accent="orange">
            <p>
              The Persona Bench is the roster of specialist agents available for brainstorm discussions. By default all
              specialists are enabled, but you can customize which ones are on the bench via{" "}
              <strong className="text-content-primary">Settings &gt; Brainstorm</strong>.
            </p>
            <p className="mt-2">
              Each persona has a unique avatar and specialty. The orchestrator selects the most relevant agents from
              your bench based on the topic you raise — so different questions will bring in different experts. You must
              keep at least 2 personas enabled.
            </p>
          </SectionCard>

          <SectionCard title="Specialist agents">
            <p>
              When you start a group discussion, specialist agents are automatically assigned from your bench based on
              your topic. Each agent gets a unique role, a color-coded avatar, and a distinct icon. The header shows
              which agents are actively participating with overlapping avatar badges and a count.
            </p>
          </SectionCard>

          <SectionCard title="Three types of messages">
            <FeatureGrid>
              <FeatureGridItem title="Your messages">
                Displayed on the left with your name — just like regular chat.
              </FeatureGridItem>
              <FeatureGridItem title="Agent messages">
                Each agent's response shows their avatar, name, role badge, and token usage. Full Markdown support and a
                copy button.
              </FeatureGridItem>
            </FeatureGrid>
            <div className="mt-3">
              <Callout variant="info" title="Orchestrator messages">
                <p>
                  System-level narration appears centered in italics, describing which agents are being called and how
                  the discussion is progressing.
                </p>
              </Callout>
            </div>
          </SectionCard>

          <SectionCard title="While agents are collaborating...">
            <p>
              A pulsing green <strong className="text-content-primary">"Discussion in progress"</strong> badge appears
              in the header, and three bouncing dots with an "Agents discussing" indicator replace the input area. The
              input is disabled while the agents are working.
            </p>
            <p className="mt-2">
              Need to jump in? The Send button is replaced by a red{" "}
              <strong className="text-content-primary">Stop</strong> button. Click it to interrupt the discussion
              immediately — the agents will stop and the conversation will be ready for your next message.
            </p>
          </SectionCard>

          <SectionCard title="Keyboard shortcuts">
            <ul className="list-disc list-inside space-y-2">
              <li>
                Press <Kbd>Enter</Kbd> to send your message.
              </li>
              <li>
                Press <Kbd>Shift</Kbd> + <Kbd>Enter</Kbd> to add a new line without sending.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Discussion history sidebar">
            <p>
              A collapsible sidebar on the right lists your recent group discussions. Active discussions sport a green
              "Active" badge, starred sessions show a yellow star, and the currently selected session is highlighted in
              orange. Collapse or expand the sidebar with the chevron toggle.
            </p>
          </SectionCard>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 5. Autopilot (Tasks)
    // -----------------------------------------------------------------------
    {
      id: "autopilot",
      title: "Autopilot",
      subtitle: "Set a goal and let AI plan and execute it step by step",
      icon: Target,
      featureFlag: "autopilot",
      content: (
        <div className="space-y-6">
          <SectionCard title="Autonomous AI, your way" accent="orange">
            <p>
              Autopilot is where AI goes from assistant to executor. Describe a goal — "Research competitor pricing
              strategies and create a summary report" — and Autopilot will plan the steps, execute them one by one, and
              deliver polished artifacts when it's done. You stay in control with human-in-the-loop input whenever the
              AI needs clarification.
            </p>
          </SectionCard>

          <SectionCard title="Creating a goal">
            <p>
              Click <strong className="text-content-primary">New Goal</strong> to open the goal creation modal:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Describe your goal</strong> — Write what you want accomplished.
                Example prompts are provided for inspiration.
              </li>
              <li>
                <strong className="text-content-primary">Select an agent</strong> — Optionally choose a specialized
                agent to handle the task.
              </li>
              <li>
                <strong className="text-content-primary">Attachments</strong> — Add files, images, SharePoint documents,
                or use voice input to provide context.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Task statuses">
            <p>Each Autopilot task progresses through statuses as it runs:</p>
            <FeatureGrid>
              <FeatureGridItem title="Pending">Goal created but not yet started.</FeatureGridItem>
              <FeatureGridItem title="Planning">
                AI is analyzing your goal and creating an execution plan.
              </FeatureGridItem>
              <FeatureGridItem title="Running">Actively executing steps in the plan.</FeatureGridItem>
              <FeatureGridItem title="Paused">Execution temporarily paused.</FeatureGridItem>
              <FeatureGridItem title="Waiting for Input">
                The AI needs your input or clarification before it can continue.
              </FeatureGridItem>
              <FeatureGridItem title="Completed">All steps finished successfully.</FeatureGridItem>
              <FeatureGridItem title="Failed">Something went wrong during execution.</FeatureGridItem>
              <FeatureGridItem title="Cancelled">You cancelled the task before it completed.</FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Task detail view" accent="purple">
            <p>Click any task to open its detail page, which shows:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Planning phase</strong> — The AI's analysis and reasoning about
                how to approach your goal.
              </li>
              <li>
                <strong className="text-content-primary">Execution plan</strong> — A numbered list of steps the AI will
                perform, shown as an expandable accordion. Each step shows its status, output, and progress.
              </li>
              <li>
                <strong className="text-content-primary">Synthesis</strong> — A final summary once all steps are
                complete.
              </li>
              <li>
                <strong className="text-content-primary">Artifacts</strong> — Deliverables produced by the task —
                documents, reports, analyses, code, data, plans, or summaries. Each artifact can be viewed and used.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Follow-up conversation">
            <p>
              After a task completes, a conversation area appears at the bottom of the task detail page. You can ask
              follow-up questions, request changes to artifacts, or explore the results further — all within the context
              of the completed task.
            </p>
          </SectionCard>

          <Callout variant="info" title="Human-in-the-loop">
            <p>
              When a task reaches "Waiting for Input" status, the AI needs your help. You'll see the question it's
              asking and can provide your response directly in the task detail view. The task resumes automatically once
              you respond.
            </p>
          </Callout>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 7. My Agents
    // -----------------------------------------------------------------------
    {
      id: "agents",
      title: "My Agents",
      subtitle: "Build, customize, and manage your AI team members",
      icon: Bot,
      content: (
        <div className="space-y-6">
          <SectionCard title="Your personal AI roster" accent="orange">
            <p>
              Agents are customized AI personas — each with its own personality and knowledge base. Use pre-built agents
              created by your team or build your own from scratch. The My Agents page shows everything available to you
              in a browsable card grid.
            </p>
          </SectionCard>

          <SectionCard title="Finding the right agent">
            <FeatureGrid>
              <FeatureGridItem title="Search">
                Filter agents by name or description using the search bar.
              </FeatureGridItem>
              <FeatureGridItem title="Filter buttons">
                Toggle between "All Agents", "My Agents" (ones you created), and "Team Agents" (shared within your
                current team).
              </FeatureGridItem>
            </FeatureGrid>
            <p className="mt-3">
              Each card shows the agent's name, description, usage count, model, and connected data sources. Click a
              card to dive into the details.
            </p>
          </SectionCard>

          <SectionCard title="Agent details view">
            <p>Clicking an agent card opens the full detail view where you can see:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Full description and system prompt.</li>
              <li>The AI model powering the agent.</li>
              <li>Visibility level (Private, Team, or Public).</li>
              <li>Usage stats and data source count.</li>
            </ul>
            <p className="mt-2">
              From here, click <strong className="text-content-primary">Edit Agent</strong> to tweak it or{" "}
              <strong className="text-content-primary">Start Chat</strong> to jump into a conversation.
            </p>
          </SectionCard>

          <SectionCard title="Creating an agent" accent="green">
            <p>
              Click <strong className="text-content-primary">Create Agent</strong> and you'll get a form to fill out:
            </p>
            <p className="mt-3 mb-2">
              <strong className="text-content-primary">General tab:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Name & Description</strong> — Give your agent an identity.
              </li>
              <li>
                <strong className="text-content-primary">Color</strong> — Pick from 6 colors (orange, purple, green,
                amber, red, cyan) for the avatar.
              </li>
              <li>
                <strong className="text-content-primary">System Prompt</strong> — This is the heart of your agent.
                Define its behavior, expertise, and personality.
              </li>
              <li>
                <strong className="text-content-primary">AI Model</strong> — Choose from the enabled models.
              </li>
              <li>
                <strong className="text-content-primary">Visibility</strong> — Private (just you), Team (your
                teammates), or Public (everyone).
              </li>
              <li>
                <strong className="text-content-primary">Data Sources</strong> — Check boxes to attach data collections
                for RAG capabilities.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Quick actions on hover">
            <p>Hover over any agent card to reveal instant action buttons:</p>
            <FeatureGrid>
              <FeatureGridItem title="Start Chat">Open a new conversation with this agent right away.</FeatureGridItem>
              <FeatureGridItem title="Edit">Modify the agent's settings and prompt.</FeatureGridItem>
              <FeatureGridItem title="Duplicate">
                Clone the agent — a copy is created with "(Copy)" appended to the name.
              </FeatureGridItem>
              <FeatureGridItem title="Delete">
                Permanently remove the agent (you'll be asked to confirm).
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 6. Workflows (Agent Flows)
    // -----------------------------------------------------------------------
    {
      id: "flows",
      title: "Workflows",
      subtitle: "Build repeatable processes by chaining agents together",
      icon: GitBranch,
      featureFlag: "workflows",
      content: (
        <div className="space-y-6">
          <SectionCard title="Automation, assembled" accent="purple">
            <p>
              Workflows let you chain multiple agents into automated processes. Each step uses a different agent, and
              the output of one step feeds directly into the next — like an assembly line for AI tasks. Run them
              manually, schedule them, or trigger them automatically.
            </p>
          </SectionCard>

          <SectionCard title="Browsing your workflows">
            <p>The workflows page shows all your workflows as cards. Here's what each card tells you:</p>
            <FeatureGrid>
              <FeatureGridItem title="Status badge">
                "Draft" (no steps yet) or "Inactive" (has steps configured).
              </FeatureGridItem>
              <FeatureGridItem title="Step & execution counts">
                How many steps the workflow has and how many times it's been run.
              </FeatureGridItem>
            </FeatureGrid>
            <div className="mt-3">
              <Callout variant="info" title="Pending reviews">
                <p>
                  A "pending review" badge appears if any runs are paused waiting for your approval. This is checked
                  automatically every 30 seconds — no need to refresh.
                </p>
              </Callout>
            </div>
          </SectionCard>

          <SectionCard title="Creating a workflow">
            <p>
              Click <strong className="text-content-primary">Create Workflow</strong> to kick off a two-step process:
            </p>
            <div className="mt-3 space-y-4">
              <StepCard step={1} title="Details">
                Enter a workflow name and an optional description.
              </StepCard>
              <StepCard step={2} title="Build">
                Use the visual Workflow Builder to add agent steps. Click "Add Agent Step" to select an agent — it
                creates a node on the canvas connected to the previous step.
              </StepCard>
            </div>
          </SectionCard>

          <SectionCard title="The Workflow Builder" accent="orange">
            <p>The Workflow Builder gives you a visual canvas for designing your workflow:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                Each agent step appears as a draggable node, connected by animated arrows showing the workflow
                direction.
              </li>
              <li>
                Click a node to open its <strong className="text-content-primary">configuration panel</strong> — add a
                custom prompt and toggle <strong className="text-content-primary">Requires Human Review</strong>.
              </li>
              <li>
                Steps marked for review show a clock icon and will pause execution for your approval before continuing.
              </li>
              <li>
                Click <strong className="text-content-primary">Save Workflow</strong> when you're done. Steps are
                ordered by their vertical position on the canvas.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Workflow detail page">
            <FeatureGrid>
              <FeatureGridItem title="Workflow steps">
                A horizontal chain of agent steps with arrows between them. Steps requiring review are marked with a
                clock icon.
              </FeatureGridItem>
              <FeatureGridItem title="Schedules">
                Configure daily, weekly, or custom schedules with time, timezone, and start date. Delete and recreate as
                needed.
              </FeatureGridItem>
              <FeatureGridItem title="Recent runs">
                The 5 most recent executions with status, tokens, and links. Paused runs are highlighted in yellow.
              </FeatureGridItem>
              <FeatureGridItem title="Paused runs alert">
                A yellow banner appears if any runs need your attention.
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Running a workflow">
            <p>From the Workflow Runner page, choose an input type and provide data:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Prompt</strong> — Enter text instructions directly.
              </li>
              <li>
                <strong className="text-content-primary">Web URL</strong> — Provide a URL to fetch and process.
              </li>
              <li>
                <strong className="text-content-primary">Data Source</strong> — Select an existing data collection.
              </li>
            </ul>
            <p className="mt-2">
              During execution, you'll see real-time streaming progress for each step with status indicators (running,
              completed, paused, error). Token usage appears per step, and the final output is displayed when all steps
              complete.
            </p>
          </SectionCard>

          <SectionCard title="Human-in-the-loop review" accent="amber">
            <p>
              When a step requires human review, the workflow pauses and opens a{" "}
              <strong className="text-content-primary">Review Modal</strong>:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Review the step's output alongside the original workflow input.</li>
              <li>
                Click <strong className="text-content-primary">Edit Output</strong> to tweak the content before
                approval.
              </li>
              <li>
                <strong className="text-content-primary">Approve & Continue</strong> (or "Approve with Edits") to
                proceed.
              </li>
              <li>
                <strong className="text-content-primary">Reject & Cancel</strong> to stop the workflow entirely.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Run history">
            <p>
              The Run History page lists all previous executions in expandable rows — each showing status, date, token
              count, and duration. Expand any row for the full details. Actions include:
            </p>
            <FeatureGrid>
              <FeatureGridItem title="Re-run">Execute the workflow again with the same input.</FeatureGridItem>
              <FeatureGridItem title="Export">Download the run details as a JSON file.</FeatureGridItem>
              <FeatureGridItem title="View Details">
                Open the full run detail page with step-by-step input/output, timing, and error info.
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 7. History
    // -----------------------------------------------------------------------
    {
      id: "history",
      title: "History",
      subtitle: "Every conversation, organized and always within reach",
      icon: History,
      content: (
        <div className="space-y-6">
          <SectionCard title="Your conversation archive" accent="orange">
            <p>
              The History page brings all your past conversations together in one searchable, filterable view — both
              chats you started and sessions shared with you. Star the important ones, revisit old ideas, and keep your
              work organized without any effort.
            </p>
          </SectionCard>

          <SectionCard title="Search & filter">
            <FeatureGrid>
              <FeatureGridItem title="Search">
                Find conversations by title using the search bar at the top.
              </FeatureGridItem>
              <FeatureGridItem title="View filter">
                Switch between "All" (everything), "My Chats" (your own), and "Shared" (sessions shared with you, with a
                count badge).
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Smart date grouping">
            <p>
              Sessions are automatically organized into intuitive date groups —{" "}
              <strong className="text-content-primary">Today</strong>,{" "}
              <strong className="text-content-primary">Yesterday</strong>,{" "}
              <strong className="text-content-primary">Last 7 Days</strong>, and{" "}
              <strong className="text-content-primary">Older</strong>. Empty groups are hidden automatically so you only
              see what matters.
            </p>
          </SectionCard>

          <SectionCard title="Session cards">
            <p>
              Each card shows the title, timestamp, message count, and model used. If an agent was involved, you'll see
              a purple "Agent" badge. Click any card to jump back into that conversation.
            </p>
            <p className="mt-2">Hover to reveal action buttons:</p>
            <FeatureGrid>
              <FeatureGridItem title="Star / Unstar">
                Mark important conversations for quick reference. Starred sessions show a filled amber star.
              </FeatureGridItem>
              <FeatureGridItem title="Open in New Tab">
                Open the conversation in a separate browser tab.
              </FeatureGridItem>
              <FeatureGridItem title="Delete">Permanently remove the conversation.</FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Shared session cards" accent="purple">
            <p>
              Sessions shared with you stand out with a purple border and a "Shared" badge. They show who shared the
              session and when, plus your permission level:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Read & Write</strong> — Purple badge. Sending a message forks
                the chat into your own copy.
              </li>
              <li>
                <strong className="text-content-primary">Read Only</strong> — Gray badge. View-only access, no
                modifications.
              </li>
            </ul>
            <p className="mt-2">Shared cards support opening in a new tab, but don't have star or delete actions.</p>
          </SectionCard>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 8. Data Sources
    // -----------------------------------------------------------------------
    {
      id: "data-sources",
      title: "Data Sources",
      subtitle: "Give your agents the knowledge they need to be brilliant",
      icon: Database,
      content: (
        <div className="space-y-6">
          <SectionCard title="Knowledge at your agents' fingertips" accent="blue">
            <p>
              Data Sources let you upload and manage documents that agents can reference during conversations — a
              technique called RAG (Retrieval-Augmented Generation). By connecting data sources to your agents, you give
              them deep context about your business, products, or domain. The result? More accurate, relevant answers
              that actually know your stuff.
            </p>
          </SectionCard>

          <SectionCard title="Browsing your data sources">
            <p>The Data Sources page shows all your collections in a card grid. Each card displays:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Type icon</strong> — File icon for uploads, globe for websites,
                cloud for SharePoint.
              </li>
              <li>
                <strong className="text-content-primary">Status badge</strong> —{" "}
                <span className="text-green-400">Ready</span> (indexed), <span className="text-blue-400">Syncing</span>{" "}
                (processing), <span className="text-red-400">Error</span> (failed), or{" "}
                <span className="text-yellow-400">Pending</span> (queued).
              </li>
              <li>Name, description, file count, and last sync date.</li>
            </ul>
            <p className="mt-2">
              Use the search bar to filter by name or description. Click a card to view its files, or use the three-dot
              menu for quick actions.
            </p>
          </SectionCard>

          <SectionCard title="Quick actions (three-dot menu)">
            <FeatureGrid>
              <FeatureGridItem title="Sync Now">
                Reprocess and re-index the data source with the latest content.
              </FeatureGridItem>
              <FeatureGridItem title="View Files">
                Open the detail page to see everything in the collection.
              </FeatureGridItem>
              <FeatureGridItem title="Delete">
                Permanently remove the data source and all its files (you'll be asked to confirm).
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Adding a data source" accent="green">
            <p>
              Click <strong className="text-content-primary">Add Data Source</strong> and choose your source type:
            </p>
            <div className="mt-3 space-y-4">
              <StepCard step={1} title="File Upload">
                Upload documents directly — PDF, DOCX, TXT, MD, CSV, and JSON. Up to 50MB per file, and you can select
                multiple at once.
              </StepCard>
              <StepCard step={2} title="Website">
                Provide a URL and the platform will download and index the content automatically.
              </StepCard>
              <StepCard step={3} title="SharePoint">
                Connect to your organization's SharePoint document libraries. Browse available sites and drives, select
                the documents you need, and the platform will download and index them automatically.
              </StepCard>
            </div>
            <p className="mt-4">
              After picking a type, enter a collection name and optional description, then provide your files or URL.
              Processing starts automatically.
            </p>
          </SectionCard>

          <SectionCard title="Data source detail page">
            <p>
              The detail page shows all files in a table — filename, size, type, status, and date added. From here you
              can:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Add Files</strong> — Upload more to the collection (for
                upload-type sources).
              </li>
              <li>
                <strong className="text-content-primary">Sync Now</strong> — Reprocess the entire collection.
              </li>
              <li>
                <strong className="text-content-primary">Download</strong> — Grab individual files to your computer.
              </li>
              <li>
                <strong className="text-content-primary">Delete files</strong> — Remove individual files from the
                collection.
              </li>
            </ul>
          </SectionCard>

          <Callout variant="tip">
            <p>
              Once created, attach data sources to agents via the agent creation/edit form. With connected data sources,
              your agents can search and reference those documents during conversations — making their answers way more
              relevant and domain-specific.
            </p>
          </Callout>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 9. Tools
    // -----------------------------------------------------------------------
    {
      id: "tools",
      title: "Tools",
      subtitle: "Extend what your agents can do with powerful integrations",
      icon: Wrench,
      adminOnly: true,
      content: (
        <div className="space-y-6">
          <SectionCard title="Give your agents superpowers" accent="orange">
            <p>
              Tools go beyond text generation — they let agents search the web, execute code, connect to external
              services, and more. The Tools page is divided into two sections: built-in tools and MCP servers.
            </p>
          </SectionCard>

          <SectionCard title="Built-in & Web Search tools">
            <p>Core tools that come with the platform. Each one appears as a row with:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Tool name, description, and type badge ("Built-in" or "Web Search").</li>
              <li>A "System" badge for platform-managed tools.</li>
              <li>
                A <strong className="text-content-primary">toggle switch</strong> to enable or disable the tool for your
                agents.
              </li>
            </ul>
            <p className="mt-2">Use the search bar at the top to find tools by name or description.</p>
          </SectionCard>

          <SectionCard title="MCP Servers" accent="purple">
            <p>
              MCP (Model Context Protocol) servers extend agent capabilities by connecting to external services. They're
              displayed as cards in a grid with status indicators:
            </p>
            <FeatureGrid>
              <FeatureGridItem title="Status">Green checkmark = enabled. Gray X = disabled.</FeatureGridItem>
              <FeatureGridItem title="Actions">
                Enable/disable, edit (non-system), or delete (non-system, with confirmation).
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Adding an MCP server" accent="green">
            <p>
              Click <strong className="text-content-primary">Add MCP Server</strong> and fill in:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Server Name</strong> and{" "}
                <strong className="text-content-primary">Description</strong>.
              </li>
              <li>
                <strong className="text-content-primary">Transport Type</strong>: Provide a server URL and optional
                headers (as JSON).
              </li>
              <li>
                <strong className="text-content-primary">Timeout</strong> — Connection timeout in seconds (1–300,
                default 60).
              </li>
            </ul>
          </SectionCard>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 11. Custom Apps
    // -----------------------------------------------------------------------
    {
      id: "apps",
      title: "Custom Apps",
      subtitle: "Pre-built AI applications ready to launch",
      icon: Grid3x3,
      featureFlag: "apps",
      content: (
        <div className="space-y-6">
          <SectionCard title="Ready-made and ready to go" accent="orange">
            <p>
              Custom Apps are purpose-built, AI-powered applications. Instead of configuring agents and prompts
              yourself, apps give you polished interfaces for common tasks — just open and go. Think of them as turnkey
              AI solutions.
            </p>
          </SectionCard>

          <SectionCard title="Browsing the catalog">
            <p>Apps are organized into categories you can filter by:</p>
            <FeatureGrid>
              <FeatureGridItem title="All">View every app in the catalog at once.</FeatureGridItem>
              <FeatureGridItem title="Document Generation">
                Apps focused on creating documents, presentations, and reports.
              </FeatureGridItem>
              <FeatureGridItem title="External Tools">
                Links to external services and applications your team uses.
              </FeatureGridItem>
            </FeatureGrid>
            <p className="mt-3">Use the search bar to filter apps by name or description.</p>
          </SectionCard>

          <SectionCard title="Available apps" accent="amber">
            <FeatureGrid>
              <FeatureGridItem title="SoWift">
                Featured app for generating Statements of Work. Launches directly within AnthmAI with a guided
                AI-powered workflow.
              </FeatureGridItem>
              <FeatureGridItem title="Presentations">AI-powered presentation generation (coming soon).</FeatureGridItem>
              <FeatureGridItem title="Skills Tracker">
                External tool for tracking team skills and competencies. Opens in a new tab.
              </FeatureGridItem>
              <FeatureGridItem title="Libby">External AI assistant. Opens in a new tab.</FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <Callout variant="info" title="Featured & external apps">
            <p>
              Featured apps appear in a prominent section with larger cards and a "Featured" badge. External apps open
              in a new browser tab, while native apps launch directly within AnthmAI. Apps marked "Coming Soon" appear
              at reduced opacity and are not yet available.
            </p>
          </Callout>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 12. Teams
    // -----------------------------------------------------------------------
    {
      id: "teams",
      title: "Teams",
      subtitle: "Organize your people and share resources effortlessly",
      icon: Users,
      content: (
        <div className="space-y-6">
          <SectionCard title="Better together" accent="blue">
            <p>
              Teams let you organize people and share resources — agents, tools, and data sources. Each team has its own
              workspace, and your current team determines which shared resources you see across the entire platform.
              Collaboration starts here.
            </p>
          </SectionCard>

          <SectionCard title="Browsing teams">
            <p>
              The Teams page displays your teams in a card grid. Each card shows the team name, description, member
              count, and up to 5 member avatar initials with a "+N" overflow indicator. There's also a "View Details"
              link to dig deeper.
            </p>
            <p className="mt-2">Use the search bar to filter teams by name or description.</p>
          </SectionCard>

          <SectionCard title="Creating a team">
            <p>
              Click <strong className="text-content-primary">Create Team</strong>, enter a name (required) and an
              optional description, then hit Create. That's it — your new team is ready for members.
            </p>
          </SectionCard>

          <SectionCard title="Team actions">
            <p>Click the three-dot menu on any team card to access:</p>
            <FeatureGrid>
              <FeatureGridItem title="View Team">See the team's full details and member list.</FeatureGridItem>
              <FeatureGridItem title="Invite Members">Add new people to the team.</FeatureGridItem>
              <FeatureGridItem title="Settings">Configure team-level settings.</FeatureGridItem>
              <FeatureGridItem title="Delete Team">Permanently remove the team.</FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <Callout variant="tip">
            <p>
              Use the team selector in the header to switch between your teams. Your view across all pages will update
              instantly to show the resources available in the selected team.
            </p>
          </Callout>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // 13. Settings
    // -----------------------------------------------------------------------
    {
      id: "settings",
      title: "Settings",
      subtitle: "Make AnthmAI yours — themes, preferences, and more",
      icon: Settings,
      content: (
        <div className="space-y-6">
          <SectionCard title="Your personal control panel" accent="orange">
            <p>
              The Settings page lets you customize every aspect of your AnthmAI experience. It's organized into seven
              tabs accessible from the sidebar on the left.
            </p>
          </SectionCard>

          <SectionCard title="Profile">
            <p>
              View your account info — display name, email, and avatar. These are synced from your Microsoft account and
              are read-only. Looking good!
            </p>
          </SectionCard>

          <SectionCard title="Appearance">
            <p>
              Pick the look that suits you. Choose between <strong className="text-content-primary">Light</strong>,{" "}
              <strong className="text-content-primary">Dark</strong>, or{" "}
              <strong className="text-content-primary">System</strong> themes. Dark mode is the default because, well,
              dark mode is the best.
            </p>
          </SectionCard>

          <SectionCard title="AI Preferences" accent="purple">
            <p>This is where you can shape how AI responds to you across every chat and agent:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Response Tone</strong> — Choose from Professional, Casual,
                Friendly, Concise, or Direct. Click to select, click again to deselect.
              </li>
              <li>
                <strong className="text-content-primary">Response Length</strong> — Brief, Moderate, or Detailed — you
                decide how verbose the AI gets.
              </li>
              <li>
                <strong className="text-content-primary">Custom Instructions</strong> — Write up to 2,000 characters of
                standing instructions that apply to all your conversations (e.g., "Always provide code examples in
                Python"). A character counter tracks your usage.
              </li>
            </ul>
            <Callout variant="tip">
              <p>
                When AI preferences are active, the Preferences button in Chat turns{" "}
                <span className="text-brand-secondary">purple</span> and shows a tooltip summarizing your settings.
              </p>
            </Callout>
          </SectionCard>

          {on("brainstorm") && (
            <SectionCard title="Brainstorm" accent="blue">
              <p>
                Configure your <strong className="text-content-primary">Persona Bench</strong> — the roster of
                specialist agents available during Brainstorm discussions. Each persona is listed with its avatar, name,
                and specialty. Check or uncheck personas to control which ones the orchestrator can draw from.
              </p>
              <p className="mt-2">
                You must keep at least 2 personas enabled at all times. Use the{" "}
                <strong className="text-content-primary">Reset to all</strong> button to re-enable every persona.
              </p>
            </SectionCard>
          )}

          <SectionCard title="Notifications">
            <p>Toggle the alerts that matter to you:</p>
            <FeatureGrid>
              <FeatureGridItem title="Email notifications">Receive updates about your agent runs.</FeatureGridItem>
              {on("workflows") && (
                <FeatureGridItem title="Workflow completion alerts">
                  Get notified when workflows finish running.
                </FeatureGridItem>
              )}
              <FeatureGridItem title="Weekly digest">A summary of your usage and activity.</FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Security">
            <p>
              Authentication is managed through <strong className="text-content-primary">Microsoft Entra ID</strong>.
              Contact your IT admin for password or MFA changes. You can also generate{" "}
              <strong className="text-content-primary">API keys</strong> for programmatic access to the platform.
            </p>
          </SectionCard>

          <SectionCard title="Teams">
            <p>
              View all the teams you belong to and your role in each (Admin, Member, etc.). Click a team to navigate to
              its details.
            </p>
          </SectionCard>
        </div>
      ),
    },

    // -----------------------------------------------------------------------
    // API Keys
    // -----------------------------------------------------------------------

    {
      id: "api-keys",
      title: "API Keys",
      subtitle: "Access Anthm's models programmatically via the OpenAI SDK",
      icon: Key,
      content: (
        <div className="space-y-6">
          <SectionCard title="What are API keys?" accent="green">
            <p>
              API keys let you use Anthm as an LLM endpoint from your own code, scripts, and tools. Instead of going
              through the chat UI, you can send requests directly to Anthm's hosted models — and all usage is still
              tracked and governed by your organization.
            </p>
            <p>
              The API is fully compatible with the{" "}
              <strong className="text-content-primary">OpenAI Python and Node SDKs</strong>. Any code that works with
              OpenAI will work with Anthm by changing the <code>base_url</code> and <code>api_key</code>.
            </p>
          </SectionCard>

          <SectionCard title="Getting started">
            <p>Follow these steps to start using the API:</p>
            <ol className="list-decimal list-inside space-y-2 mt-2">
              <li>
                Go to <strong className="text-content-primary">Settings → Security</strong> and click{" "}
                <strong className="text-content-primary">New Key</strong>.
              </li>
              <li>Give it a descriptive name (e.g., "Data pipeline script" or "VS Code extension").</li>
              <li>Copy the key immediately — it's only shown once.</li>
              <li>
                Install the OpenAI SDK: <code>pip install openai</code> (Python) or <code>npm install openai</code>{" "}
                (Node.js).
              </li>
              <li>Point the SDK at your Anthm instance and start making requests.</li>
            </ol>
          </SectionCard>

          <SectionCard title="Python example" accent="purple">
            <p>Here's a complete working example:</p>
            <pre className="mt-3 p-3 bg-surface-secondary rounded-lg text-xs font-mono text-content-secondary overflow-x-auto whitespace-pre">
              {`from openai import OpenAI

client = OpenAI(
    base_url="https://your-anthm-instance.com/ext/v1/openai",
    api_key="ak_your-key-here"
)

# Simple completion
response = client.chat.completions.create(
    model="tier:fast",
    messages=[{"role": "user", "content": "Hello!"}]
)
print(response.choices[0].message.content)

# Streaming
stream = client.chat.completions.create(
    model="tier:thorough",
    messages=[{"role": "user", "content": "Explain recursion"}],
    stream=True
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`}
            </pre>
          </SectionCard>

          <SectionCard title="Node.js example">
            <pre className="p-3 bg-surface-secondary rounded-lg text-xs font-mono text-content-secondary overflow-x-auto whitespace-pre">
              {`import OpenAI from "openai";

const client = new OpenAI({
    baseURL: "https://your-anthm-instance.com/ext/v1/openai",
    apiKey: "ak_your-key-here",
});

const response = await client.chat.completions.create({
    model: "tier:fast",
    messages: [{ role: "user", content: "Hello!" }],
});

console.log(response.choices[0].message.content);`}
            </pre>
          </SectionCard>

          <SectionCard title="Available models">
            <p>You can use model names directly or use tier shortcuts:</p>
            <FeatureGrid>
              <FeatureGridItem title="tier:fast">
                Optimized for speed — great for quick tasks, summaries, and simple questions.
              </FeatureGridItem>
              <FeatureGridItem title="tier:thorough">
                Optimized for quality — best for complex reasoning, analysis, and detailed outputs.
              </FeatureGridItem>
            </FeatureGrid>
            <p className="mt-3">
              To see all available models, call <code>client.models.list()</code> — this returns every model your
              organization has enabled.
            </p>
          </SectionCard>

          <SectionCard title="Key management">
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">One key per integration</strong> — create separate keys for
                each script or tool so you can revoke them independently.
              </li>
              <li>
                <strong className="text-content-primary">Keys are shown once</strong> — copy it immediately after
                creation. If you lose it, revoke the old key and create a new one.
              </li>
              <li>
                <strong className="text-content-primary">Revoke unused keys</strong> — if a key is no longer needed,
                revoke it from Settings → Security. Revocation is immediate.
              </li>
              <li>
                <strong className="text-content-primary">Usage tracking</strong> — all API usage is tracked per-key and
                visible to your organization's admins in the Usage dashboard.
              </li>
            </ul>
          </SectionCard>

          <Callout variant="tip">
            <p>
              The Quick Start Guide on the API Keys page (Settings → Security) includes copy-paste code snippets with
              your instance's URL pre-filled.
            </p>
          </Callout>
        </div>
      ),
    },

    ...(on("rally")
      ? [
          {
            id: "rally",
            title: "RALLY",
            subtitle: "Remote Access Link & Loopback Yoke — delegate work to a remote machine",
            icon: Server,
            content: (
              <div className="space-y-6">
                <SectionCard title="What is RALLY?" accent="purple">
                  <p>
                    <strong className="text-content-primary">RALLY (Remote Access Link &amp; Loopback Yoke)</strong>{" "}
                    lets an Anthm agent delegate a task to an always-on machine you control — a "connector" running on
                    your own computer. The machine picks up the job, runs it with real filesystem and tool access, and
                    returns the result to the agent.
                  </p>
                  <p>
                    Use it for work that must happen on a real machine: reading local files, running scripts, building a
                    deliverable, or anything that needs tools beyond the chat sandbox.
                  </p>
                </SectionCard>

                <SectionCard title="Connecting a machine">
                  <ol className="list-decimal list-inside space-y-2 mt-2">
                    <li>
                      Go to <strong className="text-content-primary">Settings → Security</strong> and create an API key
                      with the <code>harness</code> scope.
                    </li>
                    <li>Install the RALLY connector on the machine using that key.</li>
                    <li>
                      The machine appears under <strong className="text-content-primary">Settings → RALLY</strong> with
                      its live status (online / busy / offline).
                    </li>
                  </ol>
                </SectionCard>

                <SectionCard title="Security & sandboxing">
                  <p>
                    The connector runs jobs inside a locked-down sandbox: it can only read the directories you allow,
                    write to a dedicated workspace, and run a small allowlist of commands. Secrets (SSH keys,
                    credentials, <code>.env</code> files) are denied, and network access is blocked by default.
                  </p>
                  <Callout variant="tip">
                    <p>
                      You choose exactly which folders the connector may read when you install it. Everything else is
                      off-limits unless you explicitly allow it.
                    </p>
                  </Callout>
                </SectionCard>
              </div>
            ),
          },
        ]
      : []),

    // -----------------------------------------------------------------------
    // Admin-only sections
    // -----------------------------------------------------------------------

    // 14. Admin: Overview
    {
      id: "admin-overview",
      title: "Admin: Overview",
      subtitle: "The control center for platform-wide management",
      icon: Shield,
      adminOnly: true,
      content: (
        <div className="space-y-6">
          <SectionCard title="Welcome to the Admin panel" accent="purple">
            <p>
              The Admin panel is your control center for everything platform-wide. It's exclusively available to{" "}
              <strong className="text-content-primary">Platform Admins</strong> and gives you full control over user
              accounts, AI model configuration, usage monitoring, and user feedback. With great power comes a pretty
              great admin panel.
            </p>
          </SectionCard>

          <SectionCard title="Getting here">
            <p>
              The <strong className="text-content-primary">Admin</strong> link appears in the sidebar only for Platform
              Admins. If you don't see it, reach out to your organization's existing administrator to have your role
              upgraded. You'll land on the Users tab by default.
            </p>
          </SectionCard>

          <SectionCard title="The four pillars of admin">
            <FeatureGrid>
              <FeatureGridItem title="Users">
                View and manage all registered users, change roles, and activate/deactivate accounts.
              </FeatureGridItem>
              <FeatureGridItem title="Models">
                Configure which AI models are available, adjust deployment settings, and manage costs.
              </FeatureGridItem>
              <FeatureGridItem title="Usage">
                Monitor platform-wide stats — requests, active users, tokens, and estimated costs.
              </FeatureGridItem>
              <FeatureGridItem title="Feedback">
                Review bug reports and feature requests submitted by users across the platform.
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>
        </div>
      ),
    },

    // 15. Admin: User Management
    {
      id: "admin-users",
      title: "Admin: User Management",
      subtitle: "See everyone on the platform and manage their access",
      icon: Shield,
      adminOnly: true,
      content: (
        <div className="space-y-6">
          <SectionCard title="Your people, at a glance" accent="purple">
            <p>
              The Users tab shows every registered user on the platform in a searchable, paginated table. From here you
              can view details, change roles, and toggle account status — all in real-time with instant updates.
            </p>
          </SectionCard>

          <SectionCard title="User table details">
            <p>Each row shows you everything you need:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Avatar & Name</strong> — A gradient avatar circle with the
                user's first initial, plus their display name and email address.
              </li>
              <li>
                <strong className="text-content-primary">Role</strong> — A dropdown that lets you change roles
                instantly:
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>
                    <strong className="text-content-primary">Member</strong> — Standard access to all non-admin
                    features.
                  </li>
                  <li>
                    <strong className="text-content-primary">Platform Admin</strong> — Full access including this Admin
                    panel. Shown in purple text with a shield icon.
                  </li>
                </ul>
              </li>
              <li>
                <strong className="text-content-primary">Status</strong> — A clickable badge toggling between{" "}
                <span className="text-green-400">Active</span> (green checkmark) and{" "}
                <span className="text-content-tertiary">Inactive</span> (gray X). One click to activate or deactivate.
              </li>
              <li>
                <strong className="text-content-primary">Last Login</strong> — Relative time ("Just now", "5 minutes
                ago", "3 days ago") or "Never" if they haven't signed in yet.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Search & pagination">
            <p>
              Use the search bar to filter by name or email. A total user count is displayed on the right. Results are
              paginated at 20 users per page with <strong className="text-content-primary">Previous</strong> /{" "}
              <strong className="text-content-primary">Next</strong> buttons and a page indicator. Searching resets you
              to page 1 automatically.
            </p>
          </SectionCard>
        </div>
      ),
    },

    // 16. Admin: Model Configuration
    {
      id: "admin-models",
      title: "Admin: Model Configuration",
      subtitle: "Control which AI models power the platform",
      icon: Shield,
      adminOnly: true,
      content: (
        <div className="space-y-6">
          <SectionCard title="The AI model lineup" accent="purple">
            <p>
              The Models tab puts you in charge of which AI models are available across the platform. Models appear in a
              card grid — enable or disable them, fine-tune their configuration, or add new ones.
            </p>
          </SectionCard>

          <SectionCard title="Model cards">
            <p>Each model card shows:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Name & Provider</strong> — The display name and provider (Azure
                OpenAI or Azure AI Foundry).
              </li>
              <li>
                <strong className="text-content-primary">Enable/Disable toggle</strong> — A badge in the top-right
                corner. Click to switch between <span className="text-green-400">Enabled</span> (green eye) and{" "}
                <span className="text-content-tertiary">Disabled</span> (crossed-out eye). Disabled models are hidden
                from users.
              </li>
              <li>
                <strong className="text-content-primary">Context Window</strong> — Max input tokens supported.
              </li>
              <li>
                <strong className="text-content-primary">Max Output</strong> — Max tokens per response.
              </li>
              <li>
                <strong className="text-content-primary">Capabilities</strong> — Tags showing supported features (chat,
                vision, function_calling, etc.).
              </li>
              <li>
                <strong className="text-content-primary">Configure</strong> — Opens the configuration modal.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="Configuring a model" accent="amber">
            <p>
              Click <strong className="text-content-primary">Configure</strong> on any card to adjust:
            </p>
            <FeatureGrid>
              <FeatureGridItem title="Model & Deployment Name">
                The display name shown to users and the Azure deployment identifier for API calls.
              </FeatureGridItem>
              <FeatureGridItem title="Token Limits">Context window (max input) and max output tokens.</FeatureGridItem>
              <FeatureGridItem title="Cost per 1K Tokens">
                Dollar amounts for input and output tokens, used for cost estimation across the platform.
              </FeatureGridItem>
              <FeatureGridItem title="Enable checkbox">Toggle whether the model is available to users.</FeatureGridItem>
            </FeatureGrid>
            <p className="mt-3">
              Click <strong className="text-content-primary">Save Changes</strong> to apply.
            </p>
          </SectionCard>

          <SectionCard title="Adding a new model" accent="green">
            <p>
              Click <strong className="text-content-primary">Add Model</strong> at the top of the page to register a new
              AI model:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Model ID</strong> — Unique identifier (e.g., "model-router").
              </li>
              <li>
                <strong className="text-content-primary">Display Name</strong> — What users see in model selectors.
              </li>
              <li>
                <strong className="text-content-primary">Provider</strong> — Azure OpenAI or Azure AI Foundry.
              </li>
              <li>
                <strong className="text-content-primary">Deployment Name</strong> — Azure deployment name for API
                routing.
              </li>
              <li>
                <strong className="text-content-primary">Capabilities</strong> — Add tags by typing and pressing{" "}
                <Kbd>Enter</Kbd> or clicking Add. Remove with X.
              </li>
              <li>
                <strong className="text-content-primary">Token limits & cost</strong> — Context window, max output, and
                per-1K pricing.
              </li>
              <li>
                <strong className="text-content-primary">Enable</strong> — Whether it's immediately available (enabled
                by default).
              </li>
            </ul>
          </SectionCard>
        </div>
      ),
    },

    // 17. Admin: Usage & Monitoring
    {
      id: "admin-usage",
      title: "Admin: Usage & Monitoring",
      subtitle: "Track adoption, tokens, and costs across the organization",
      icon: Shield,
      adminOnly: true,
      content: (
        <div className="space-y-6">
          <SectionCard title="The numbers that matter" accent="purple">
            <p>
              The Usage tab gives you a complete picture of how the platform is being used across your organization.
              Track adoption, monitor token consumption, and keep costs in check — all from one dashboard.
            </p>
          </SectionCard>

          <SectionCard title="Summary stats">
            <p>Four cards at the top give you the headline numbers:</p>
            <FeatureGrid>
              <FeatureGridItem title="Total Requests">
                Total API requests made across all users during the selected time period.
              </FeatureGridItem>
              <FeatureGridItem title="Active Users">
                Users who've made at least one request during the period.
              </FeatureGridItem>
              <FeatureGridItem title="Tokens Used">
                Combined input/output token consumption. Large numbers are abbreviated (e.g., "1.2M").
              </FeatureGridItem>
              <FeatureGridItem title="Estimated Cost">
                Total estimated cost in USD based on per-model token pricing.
              </FeatureGridItem>
            </FeatureGrid>
          </SectionCard>

          <SectionCard title="Usage by team">
            <p>
              A horizontal bar chart breaks down token usage by team. Each team shows its name, token count, and a
              gradient progress bar proportional to the highest-usage team. Use the{" "}
              <strong className="text-content-primary">time range selector</strong> to switch between:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong className="text-content-primary">Last 7 days</strong>
              </li>
              <li>
                <strong className="text-content-primary">Last 30 days</strong> (default)
              </li>
              <li>
                <strong className="text-content-primary">Last 90 days</strong>
              </li>
            </ul>
            <Callout variant="info">
              <p>
                Changing the time range refreshes <em>all</em> stats on the page, not just the team chart.
              </p>
            </Callout>
          </SectionCard>

          <SectionCard title="Usage by model">
            <p>
              A table below the team chart shows per-model usage. Each row displays the model name, request count, token
              count, and estimated cost. Great for understanding which models are driving the most usage and cost.
            </p>
          </SectionCard>
        </div>
      ),
    },

    // 20. Admin: Feedback
    {
      id: "admin-feedback",
      title: "Admin: Feedback",
      subtitle: "Review bug reports and feature requests from users",
      icon: MessageCircle,
      adminOnly: true,
      content: (
        <div className="space-y-6">
          <SectionCard title="The voice of your users" accent="purple">
            <p>
              The Feedback tab gives you visibility into what users are reporting — bug reports and feature requests
              submitted through the platform. Stay on top of issues and understand what your users need most.
            </p>
          </SectionCard>

          <SectionCard title="Reviewing feedback">
            <p>Feedback items are displayed in a list, each showing:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-content-primary">Type</strong> — Whether it's a bug report or a feature request.
              </li>
              <li>
                <strong className="text-content-primary">Description</strong> — The user's detailed feedback.
              </li>
              <li>
                <strong className="text-content-primary">Submitted by</strong> — Who submitted it and when.
              </li>
            </ul>
          </SectionCard>

          <Callout variant="tip">
            <p>
              Users can submit feedback from anywhere in the platform using the feedback button. Encourage your team to
              report issues — it helps make AnthmAI better for everyone.
            </p>
          </Callout>
        </div>
      ),
    },
  ];
}
